import React, { useState, useEffect } from 'react';
import { Table, Row, Col, message, Form, Input, InputNumber, Select, Button } from 'antd';
import { EditOutlined, SaveOutlined, CloseOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { createCancellationPolicy, deleteCancellationPolicy, getCancellationPolicyByOperatorCode, updateCancellationPolicy } from '../../actions/admin.action';
import { showConfirm } from '../../components/custom-components/custom-component';
import './../../styles/vehicle-management.style.scss';

const { Option } = Select;

export const CancellationPolicyPage = () => {
    const [cancellationPolicies, setCancellationPolicies] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editingKey, setEditingKey] = useState('');
    const [form] = Form.useForm();

    useEffect(() => {
        fetchCancellationPolicy();
    }, []);

    const fetchCancellationPolicy = () => {
        setLoading(true);
        getCancellationPolicyByOperatorCode(
            (data) => {
                let policy = data.cancellationPolicy;
                setLoading(false);
                if (Array.isArray(policy)) {
                    setCancellationPolicies(policy.map((item, index) => ({ ...item, key: item.id || index })));
                } else {
                    setCancellationPolicies([]);
                    message.warning("No cancellation policy data available");
                }
            },
            (error) => {
                setLoading(false);
                message.error(error || "Failed to fetch cancellation policy");
            }
        );
    };

    const isEditing = (record) => {
        return record.key === editingKey;
    };

    const edit = (record) => {
        form.setFieldsValue({
            fromValue: record.fromValue,
            toValue: record.toValue,
            deductionAmount: record.deductionAmount,
            is_percentage: record.is_percentage === true || record.is_percentage === 1 || record.is_percentage === '1' ? '1' : '0',
            operatorName: record.operator_name || record.operatorName || '',
            policyPattern: record.policyPattern || ''
        });
        setEditingKey(record.key);
    };

    const cancel = () => {
        const editingRecord = cancellationPolicies.find((record) => record.key === editingKey);
        // If user cancels a brand-new (unsaved) row, remove it from the table.
        if (
            editingRecord &&
            (editingRecord.id === null || editingRecord.id === undefined)
        ) {
            setCancellationPolicies((prev) =>
                prev.filter((record) => record.key !== editingKey)
            );
        }
        setEditingKey('');
        form.resetFields();
    };

    const save = (record) => {
        form.validateFields()
            .then((values) => {
                const isNew = record.id === null || record.id === undefined;
                const updatedData = {
                    id: record.id,
                    operator_name: values.operatorName || record.operator_name || record.operatorName || '',
                    operator_code: global.operatorCode,
                    fromValue: values.fromValue,
                    toValue: values.toValue,
                    deductionAmount: values.deductionAmount,
                    policyPattern: values.policyPattern || record.policyPattern || '',
                    is_percentage: values.is_percentage === '1' || values.is_percentage === 1 || values.is_percentage === true
                };

                showConfirm(
                    () => {
                        const action = isNew ? createCancellationPolicy : updateCancellationPolicy;
                        action(
                            updatedData,
                            (successMessage) => {
                                message.success(successMessage || (isNew ? "Cancellation policy created successfully" : "Cancellation policy updated successfully"));
                                setEditingKey('');
                                fetchCancellationPolicy();
                            },
                            (errorMessage) => {
                                message.error(errorMessage || (isNew ? "Failed to create cancellation policy" : "Failed to update cancellation policy"));
                            }
                        );
                    },
                    isNew ? "Create Cancellation Policy" : "Update Cancellation Policy",
                    isNew
                        ? "Are you sure you want to create this cancellation policy?"
                        : "Are you sure you want to update this cancellation policy?",
                    "Yes"
                );
            })
            .catch((err) => {
                console.log('Validate Failed:', err);
            });
    };

    const addNewPolicy = () => {
        const newKey = `new-${Date.now()}`;
        const newRecord = {
            id: null,
            key: newKey,
            fromValue: null,
            toValue: null,
            deductionAmount: null,
            is_percentage: '0',
            operator_name: '',
            operatorName: '',
            policyPattern: ''
        };

        setCancellationPolicies((prev) => [...prev, newRecord]);
        setEditingKey(newKey);

        form.setFieldsValue({
            fromValue: null,
            toValue: null,
            deductionAmount: null,
            is_percentage: '0',
            operatorName: '',
            policyPattern: ''
        });
    };

    const onDelete = (record) => {
        if (record.id === null || record.id === undefined) return;

        showConfirm(
            () => {
                deleteCancellationPolicy(
                    { id: record.id },
                    (successMessage) => {
                        message.success(successMessage || "Cancellation policy deleted successfully");
                        fetchCancellationPolicy();
                    },
                    (errorMessage) => {
                        message.error(errorMessage || "Failed to delete cancellation policy");
                    }
                );
            },
            "Delete Cancellation Policy",
            "Are you sure you want to delete this cancellation policy?",
            "Yes"
        );
    };

    const EditableCell = ({
        editing,
        dataIndex,
        title,
        inputType,
        record,
        children,
        ...restProps
    }) => {
        let inputNode;
        
        if (dataIndex === 'is_percentage') {
            inputNode = (
                <Select>
                    <Option value="1">Yes</Option>
                    <Option value="0">No</Option>
                </Select>
            );
        } else if (dataIndex === 'fromValue' || dataIndex === 'toValue' || dataIndex === 'deductionAmount') {
            inputNode = <InputNumber style={{ width: '100%' }} />;
        } else {
            inputNode = <Input />;
        }

        return (
            <td {...restProps}>
                {editing ? (
                    <Form.Item
                        name={dataIndex}
                        style={{ margin: 0 }}
                        rules={[
                            {
                                required: true,
                                message: `Please input ${title}!`,
                            },
                        ]}
                    >
                        {inputNode}
                    </Form.Item>
                ) : (
                    children
                )}
            </td>
        );
    };

    const columns = [
        {
            title: 'From Value',
            dataIndex: 'fromValue',
            key: 'fromValue',
            editable: true,
            render: (text) => text !== null && text !== undefined ? text : '-'
        },
        {
            title: 'To Value',
            dataIndex: 'toValue',
            key: 'toValue',
            editable: true,
            render: (text) => text !== null && text !== undefined ? text : '-'
        },
        {
            title: 'Deduction Amount',
            dataIndex: 'deductionAmount',
            key: 'deductionAmount',
            editable: true,
            render: (text) => text !== null && text !== undefined ? text : '-'
        },
        {
            title: 'Is Percentage',
            dataIndex: 'is_percentage',
            key: 'is_percentage',
            editable: true,
            render: (text) => {
                if (text === true || text === 1 || text === '1') {
                    return 'Yes';
                } else if (text === false || text === 0 || text === '0') {
                    return 'No';
                }
                return '-';
            }
        },
        {
            title: 'Action',
            dataIndex: 'action',
            key: 'action',
            render: (_, record) => {
                const editable = isEditing(record);
                return editable ? (
                    <span>
                        <Button
                            type="text"
                            icon={<SaveOutlined />}
                            onClick={() => save(record)}
                            style={{ marginRight: 8 }}
                        >
                            Save
                        </Button>
                        <Button
                            type="text"
                            icon={<CloseOutlined />}
                            onClick={cancel}
                        >
                            Cancel
                        </Button>
                    </span>
                ) : (
                    <span>
                        <Button
                            type="text"
                            icon={<EditOutlined />}
                            onClick={() => edit(record)}
                            disabled={editingKey !== ''}
                        >
                            Edit
                        </Button>
                        <Button
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => onDelete(record)}
                            disabled={editingKey !== '' || record.id === null || record.id === undefined}
                            style={{ marginLeft: 8 }}
                        >
                            Delete
                        </Button>
                    </span>
                );
            },
        },
    ];

    const mergedColumns = columns.map((col) => {
        if (!col.editable) {
            return col;
        }
        return {
            ...col,
            onCell: (record) => ({
                record,
                inputType: col.dataIndex === 'is_percentage' ? 'select' : 'number',
                dataIndex: col.dataIndex,
                title: col.title,
                editing: isEditing(record),
            }),
        };
    });

    return (
        <div className="vehicle-management-root">
            <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
                <Col>
                    <h2>Cancellation Policy</h2>
                </Col>
                <Col>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={addNewPolicy}
                        disabled={editingKey !== ''}
                    >
                        Add
                    </Button>
                </Col>
            </Row>

            <Form form={form} component={false}>
                <Table
                    loading={loading}
                    components={{
                        body: {
                            cell: EditableCell,
                        },
                    }}
                    dataSource={cancellationPolicies}
                    columns={mergedColumns}
                    pagination={{ pageSize: 10 }}
                />
            </Form>
        </div>
    );
};

